import {
  ComponentRendering,
  LayoutServiceContext,
  LayoutServiceData,
  PlaceholdersData,
} from '@sitecore-jss/sitecore-jss-nextjs';

import { isGenericObjectType } from 'lib/typescript';
import { ValidHeadingLevelOptions, ValidHeadingLevels, WithFields } from './_types';
import { ComponentRenderingWithPlaceholders, ComponentWithContextProps } from './component-props';
import { SitecorePageProps } from './page-props';

/**
 * Typeguard function to determine if the input is a valid heading level
 * @param value Value to check
 * @returns {value is ValidHeadingLevels} True if the value is a valid heading level
 */
export const isValidHeadingLevel = (value: unknown): value is ValidHeadingLevels =>
  typeof value === 'string' && ValidHeadingLevelOptions.indexOf(value as ValidHeadingLevels) > -1;
/**
 * Sitecore context value shape
 */
export const isLayoutServiceData = (input: unknown): input is LayoutServiceData =>
  isGenericObjectType(input, [['sitecore', 'object']]) &&
  isGenericObjectType(input.sitecore, [
    ['route', 'object'],
    ['context', 'object'],
  ]);
/**
 * Checks if the input is a valid LayoutServiceContext
 * @param input Value to check
 * @returns {input is LayoutServiceContext} True if the input is a valid LayoutServiceContext
 */
export const isNmdpSitecoreContext = (input: unknown): input is LayoutServiceContext =>
  isLayoutServiceData(input) &&
  isGenericObjectType(input, [
    ['itemId', 'object'],
    ['itemPath', 'object'],
    ['route', 'object'],
  ]);
/**
 * Checks if the input is a valid SitecorePageProps
 * @param input Value to check
 * @returns {input is SitecorePageProps} True if the input is a valid SitecorePageProps
 */
export const isSitecorePageProps = (input: unknown): input is SitecorePageProps =>
  isGenericObjectType(input, [
    ['algoliaServerState', 'object'],
    ['isSearchRobot', 'boolean'],
    ['componentProps', 'object'],
    ['dictionary', 'object'],
    ['layoutData', 'object'],
    ['session', 'object'],
    ['locale', 'string'],
    ['notFound', 'boolean'],
    ['joinCode', 'string'],
  ]);
/**
 * Checks if the input is a valid SitecoreContextValue
 * @param input Value to check
 * @returns {LayoutServiceData | Omit<LayoutServiceData, 'updateSitecoreContext'>} True if the input is a valid readable SitecoreContextValue
 */
export const isLayoutServiceContextData = (input: unknown): input is LayoutServiceContext =>
  isGenericObjectType(
    input,
    [
      ['itemPath', 'string'],
      ['searchPageUrl', 'string'],
      ['knownOktaRoles', 'object'],
      ['allowedOktaRoles', 'object'],
      ['RITN', 'boolean'],
      ['siteSpecificClass', 'string'],
      ['cartLink', 'object'],
      ['redirect', 'string'],
      ['redirectIsPermanent', 'boolean'],
      ['notFound', 'boolean'],
      ['gtmPageType', 'string'],
      ['txSearchIds', 'object'],
      ['wcPath', 'string'],
    ],
    3
  );

/**
 * Type guard to check if the given input is of type SitecoreState.
 *
 * @param input - The value to be checked.
 * @returns A boolean indicating whether the input is an object containing
 *          a `sitecore` property of type `LayoutServiceData` and an optional
 *          `updateSitecoreContext` method.
 */
export const isSitcoreState = (
  input: unknown
): input is {
  sitecore: LayoutServiceData;
  updateSitecoreContext?: (value: LayoutServiceData) => void;
} => isGenericObjectType(input, [['sitecore', 'object']]);

/**
 * Type guard to determine if the given input is of type `ComponentWithContextProps`.
 *
 * This function checks if the input is a generic object type with a `sitecoreContext`
 * property of type `object` and validates that the `sitecoreContext` property conforms
 * to the `LayoutServiceContextData` type.
 *
 * @param input - The value to be checked.
 * @returns A boolean indicating whether the input is of type `ComponentWithContextProps`.
 */
export const isComponentWithContext = (input: unknown): input is ComponentWithContextProps =>
  isGenericObjectType(input, [['sitecoreContext', 'object']]) &&
  isLayoutServiceContextData(input.sitecoreContext);

/**
 * Type guard function to check if a given value is of type `PlaceholdersData`.
 *
 * @param check - The value to be checked.
 * @returns A boolean indicating whether the value is an object with at least one key,
 *          and all its properties are objects.
 */
export const isPlaceholders = (check: unknown): check is PlaceholdersData => {
  if (typeof check !== 'object' || check == null) {
    return false;
  }
  const keys = Object.keys(check);
  return (
    keys.length > 0 && keys.every((key) => typeof check[key as keyof typeof check] === 'object')
  );
};

/**
 * Determines if the provided value is a `ComponentRenderingWithPlaceholders`.
 *
 * This type guard checks if the given `check` parameter is an object that contains
 * a `componentName` property of type `string` and a `placeholders` property that
 * satisfies the `isPlaceholders` function.
 *
 * @param check - The value to be checked.
 * @returns A boolean indicating whether the value is a `ComponentRenderingWithPlaceholders`.
 */
export const isComponentRenderingWithPlaceholders = (
  check: unknown
): check is ComponentRenderingWithPlaceholders => {
  if (typeof check !== 'object' || check == null) {
    return false;
  }
  if (!('componentName' in check) || typeof check.componentName !== 'string') {
    return false;
  }
  return 'placeholders' in check && isPlaceholders(check.placeholders);
};

/**
 * Type guard to determine if a given rendering is a `ComponentRendering` with a specific `componentName`.
 *
 * @template TComponentName - The type of the component name.
 * @param rendering - The object to check.
 * @param componentName - The expected name of the component.
 * @returns A boolean indicating whether the rendering is a `ComponentRendering` with the specified `componentName`.
 */
export const isNamedComponentRendering = <TComponentName extends string>(
  rendering: unknown,
  componentName: TComponentName
): rendering is ComponentRendering & { componentName: TComponentName } => {
  if (typeof rendering !== 'object' || rendering == null) {
    return false;
  }
  if (!('componentName' in rendering) || typeof rendering.componentName !== 'string') {
    return false;
  }
  return rendering.componentName === componentName;
};

type OpType = { required: boolean | undefined };
type RequiredOrOptional<TRet = LayoutServiceContext> = <TOpRequired extends boolean | undefined>(
  input: unknown,
  props?: OpType & { required: TOpRequired }
) => TOpRequired extends true ? TRet : TRet | undefined;

/**
 * Typeguard-esque function retrieve sitecore context from input
 * @param input Value to evaluate.
 * @returns {boolean} true if the input is a wildcard item
 */
export const asLayoutServiceData: RequiredOrOptional = <TOpRequired extends boolean | undefined>(
  input: unknown,
  props?: OpType & { required: TOpRequired }
) => {
  const { required } = props ?? ({} as { required: TOpRequired });
  const checkNull = (
    check?: LayoutServiceContext
  ): TOpRequired extends true ? LayoutServiceContext : LayoutServiceContext | undefined => {
    if (!check && required === true) {
      throw new Error('Invalid Sitecore context');
    }
    return check as TOpRequired extends true
      ? LayoutServiceContext
      : LayoutServiceContext | undefined;
  };
  // Is this a Sitecore state object?
  if (isSitcoreState(input)) {
    // Yes - return the sitecore property
    return checkNull(input?.sitecore?.sitecore?.context);
  }
  if (isLayoutServiceData(input)) {
    return checkNull(input?.sitecore?.context);
  }
  if (isLayoutServiceContextData(input)) {
    return checkNull(input);
  }
  return checkNull();
};

/**
 * Typeguard-esque function to determine if the input is a wildcard item
 * @param input Value to evaluate.
 * @returns {boolean} true if the input is a wildcard item
 */
export const isWildcard = <
  T extends LayoutServiceData['sitecore'] | LayoutServiceData | LayoutServiceContext | unknown
>(
  input: T
): boolean => {
  return isGenericObjectType(asLayoutServiceData(input), [
    ['wcPath', 'string'],
    ['itemPath', 'string'],
  ]);
};

/**
 * Typeguard-esque function to determine if the input is a wildcard item
 * @param input Value to evaluate.
 * @returns {boolean} true if the input is a wildcard item
 */
export const asWildcardRoute = (input: unknown): LayoutServiceContext | undefined => {
  const ctx = asLayoutServiceData(input);
  return ctx && isWildcard(ctx) ? ctx : undefined;
};

/**
 * Type guard to check if the given input is an object that conforms to the `WithFields<T>` structure.
 *
 * This function verifies that the input is a generic object type and contains a `fields` property
 * of type `object`.
 *
 * @typeParam T - The type of the fields contained within the `WithFields` structure.
 * @param input - The value to be checked.
 * @returns A boolean indicating whether the input is of type `WithFields<T>`.
 */
export const isWithFields = <T>(input: unknown): input is WithFields<T> =>
  isGenericObjectType(input, [['fields', 'object']]);
